import type { ReactNode } from "react";
import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

/* ── Page 5 ── */

const p5Faqs: LearnFAQ[] = [
  {
    question: "Is SAINT TMS covered by insurance?",
    answer:
      "Sometimes, and it depends on your plan. SAINT is billed under Category III CPT codes 0889T–0892T. CMS kept hospital outpatient payment assignments for these codes for 2026, and the manufacturer, Magnus Medical, reports Medicare fee-for-service reimbursement and growing commercial coverage. However, many plans still treat SAINT as investigational, prior authorization is common, and many patients self-pay. The only way to know your situation is verification and, where required, prior authorization.",
  },
  {
    question: "How much does TMS therapy cost?",
    answer:
      "Cost depends on the number of sessions, mapping, physician time, the setting, and for SAINT, an MRI. Published estimates for a full conventional TMS course in the United States commonly fall in the low-to-mid five figures before insurance, and SAINT is typically priced higher because of the MRI and 50-session schedule. Neuro Wellness TMS Centers of America provides a written estimate after verifying your benefits rather than publishing a single price.",
  },
  {
    question: "Does Blue Cross cover TMS therapy?",
    answer:
      "Blue Cross Blue Shield is a federation of independent plans, so the answer is plan-specific. Most BCBS plans have a written medical policy for repetitive TMS for major depressive disorder, with criteria such as failed antidepressant trials and psychiatrist oversight, and many require prior authorization. Their positions on SAINT's Category III codes vary. Call the number on your card, ask for the TMS medical policy, and have your clinic verify benefits.",
  },
  {
    question: "How do I get TMS covered by insurance?",
    answer:
      "Ask your plan for its TMS medical policy, then work with a psychiatrist to document each criterion: a diagnosis of major depressive disorder with severity scores, each antidepressant trial with dose and duration, any psychotherapy, and the absence of exclusions. Your clinic submits this for prior authorization if required. If denied, request the reason, ask your psychiatrist about a peer-to-peer review, and file an appeal within the deadline.",
  },
  {
    question: "Does Medicare cover TMS in Florida?",
    answer:
      "Original Medicare covers repetitive TMS for major depressive disorder under Local Coverage Determinations issued by the Medicare Administrative Contractor, which in Florida is First Coast Service Options. The LCD sets medical-necessity criteria such as failed medication trials and psychiatrist supervision. You can read the current LCD on the CMS Medicare Coverage Database. Medicare Advantage plans must cover the same services but may add their own prior-authorization steps.",
  },
  {
    question:
      "What is the difference between insurance verification and prior authorization?",
    answer:
      "Verification is a benefits check: the clinic confirms your plan is active and asks whether the codes are covered, what your cost-sharing is, and whether authorization is needed. Prior authorization is a clinical review in which the plan decides, based on your records, that treatment appears medically necessary. Neither guarantees payment; a claim can still be denied for coding, setting, or eligibility reasons.",
  },
  {
    question: "Can I use my HSA or FSA to pay for TMS?",
    answer:
      "Generally, yes. Physician-prescribed, medically necessary treatment such as TMS is typically an eligible expense for health savings accounts and health flexible spending accounts, including deductibles, coinsurance, and self-pay balances. Eligibility rules come from the IRS and your account administrator, so confirm with them and keep itemized receipts. This is general information rather than tax advice.",
  },
  {
    question: "What if my insurance denies SAINT but covers regular TMS?",
    answer:
      "This is a common situation. The clinical eligibility criteria for the two treatments overlap heavily, so most people who qualify for SAINT also qualify for conventional TMS, which is covered under established codes when medical-necessity criteria are met. Discuss the trade-offs with your psychiatrist: conventional TMS takes 4 to 6 weeks of daily visits, while SAINT is 5 days but may be self-pay. You can also appeal the SAINT denial.",
  },
];

export const page5: LearnPageData = {
  slug: "saint-tms-cost-insurance-coverage",
  title: "Is SAINT TMS Covered by Insurance? TMS Cost Explained",
  description:
    "How conventional TMS and SAINT are covered, the CPT codes insurers use, verification vs. prior authorization, cost drivers, and appeals. No coverage promises.",
  canonical: "/learn/saint-tms-cost-insurance-coverage",
  h1: "Is SAINT® TMS Covered by Insurance, and What Does TMS Cost?",
  breadcrumbLabel: "SAINT TMS Cost & Insurance",
  crumbs: baseCrumbs("SAINT TMS Cost & Insurance"),
  shortAnswer:
    "Conventional TMS is broadly covered by commercial insurance, Medicare, and the VA when medical-necessity criteria are met. SAINT® uses Category III CPT codes and coverage varies by plan — many patients self-pay. Neuro Wellness verifies your benefits at no cost. Call 954-827-9793.",
  keyTakeaways: [
    "Conventional TMS is billed under CPT 90867–90869 and is broadly covered when criteria are met.",
    "SAINT® is billed under Category III CPT codes 0889T–0892T; coverage varies by plan and many patients self-pay.",
    "Verification (benefits check) and prior authorization (clinical review) are different steps; neither guarantees payment.",
    "Most BCBS plans have a written TMS medical policy; Medicare covers TMS under LCDs (First Coast in Florida).",
    "If SAINT® is denied but conventional TMS is covered, most SAINT-eligible patients also qualify for conventional TMS.",
  ],
  sections: [
    {
      heading: "How conventional TMS is covered",
      body: (
        <>
          {P(
            "Conventional repetitive TMS is billed under CPT codes 90867 (initial treatment, including mapping), 90868 (subsequent treatment), and 90869 (treatment management). It is broadly covered by commercial insurance, Medicare, and the VA for treatment-resistant depression when medical-necessity criteria are met — typically including failed antidepressant trials, psychiatrist oversight, and severity scores.",
          )}
        </>
      ),
    },
    {
      heading: "How SAINT® is covered",
      body: (
        <>
          {P(
            "SAINT® is billed under Category III CPT codes 0889T–0892T, which are newer tracking codes. CMS kept hospital outpatient payment assignments for these codes for 2026, and Magnus Medical reports Medicare fee-for-service reimbursement and growing commercial coverage. However, many plans still treat SAINT® as investigational, prior authorization is common, and many patients self-pay.",
          )}
        </>
      ),
    },
    {
      heading: "Regulatory status and billing codes",
      body: (
        <div className="my-6 rounded-xl border border-medium-blue/30 bg-medium-blue/5 p-5">
          <p>
            <strong>Regulatory status.</strong> TMS devices are FDA-cleared
            (510(k)), not FDA-approved. Conventional TMS is billed under CPT
            90867–90869 (Category I codes with established payment). SAINT® is
            billed under Category III CPT codes 0889T–0892T (tracking codes for
            emerging technologies). Category III codes do not guarantee payment
            — they tell insurers the service exists. Coverage for SAINT® varies
            by plan and many patients self-pay.
          </p>
        </div>
      ),
    },
    {
      heading: "Verification vs prior authorization",
      body: (
        <>
          {P(
            "Verification is a benefits check: the clinic confirms your plan is active and asks whether the codes are covered, what your cost-sharing is, and whether authorization is needed. Prior authorization is a clinical review in which the plan decides, based on your records, that treatment appears medically necessary. Neither guarantees payment; a claim can still be denied for coding, setting, or eligibility reasons.",
          )}
        </>
      ),
    },
    {
      heading: "What drives cost",
      body: (
        <>
          {P(
            "Cost depends on the number of sessions, mapping, physician time, the setting, and for SAINT®, an MRI. Published estimates for a full conventional TMS course in the United States commonly fall in the low-to-mid five figures before insurance, and SAINT® is typically priced higher because of the MRI and 50-session schedule. Neuro Wellness TMS Centers of America provides a written estimate after verifying your benefits rather than publishing a single price.",
          )}
        </>
      ),
    },
    {
      heading: "If you are denied",
      body: (
        <>
          {P(
            "If denied, request the reason, ask your psychiatrist about a peer-to-peer review, and file an appeal within the deadline. If SAINT® is denied but conventional TMS is covered, the clinical eligibility criteria overlap heavily — most people who qualify for SAINT® also qualify for conventional TMS, which takes 4 to 6 weeks of daily visits instead of 5 days.",
          )}
        </>
      ),
    },
  ],
  faqs: p5Faqs,
  sources: [
    {
      citation:
        "CMS Medicare Coverage Database — Local Coverage Determinations for TMS.",
      url: "https://www.cms.gov/medicare-coverage-database/",
    },
    {
      citation:
        "American Medical Association — CPT Category III codes 0889T–0892T.",
      url: "https://www.ama-assn.org/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
  ],
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#webpage`,
        name: "Is SAINT® TMS Covered by Insurance, and What Does TMS Cost?",
        url: `${SITE_URL}/learn/saint-tms-cost-insurance-coverage`,
        description:
          "How conventional TMS and SAINT are covered, the CPT codes insurers use, verification vs. prior authorization, cost drivers, and appeals. No coverage promises.",
        about: [
          {
            "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#saint`,
          },
          { "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#rtms` },
          {
            "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#condition`,
          },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#saint`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the FDA-cleared Magnus Neuromodulation System with SAINT Technology (510(k) K220177); billed under Category III CPT codes 0889T–0892T.",
        code: [
          { "@type": "MedicalCode", code: "0889T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0890T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0891T", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "0892T", codingSystem: "CPT" },
        ],
      },
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#rtms`,
        name: "Repetitive transcranial magnetic stimulation (rTMS)",
        description:
          "Conventional TMS for major depressive disorder, typically one session per weekday for 4 to 6 weeks; billed under CPT 90867, 90868, and 90869.",
        code: [
          { "@type": "MedicalCode", code: "90867", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "90868", codingSystem: "CPT" },
          { "@type": "MedicalCode", code: "90869", codingSystem: "CPT" },
        ],
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#condition`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(
        p5Faqs,
        `${SITE_URL}/learn/saint-tms-cost-insurance-coverage#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("SAINT TMS Cost & Insurance")),
    ],
  },
  relatedLinks: sharedRelated,
};
